import { z } from 'zod';

import type {
  AdminResource,
  ResourceBookingMode,
  ResourceBookingRules,
  ResourceKind,
  ResourceOperatingHour,
  ResourceWeekday,
} from '@mds/shared/types/resource';
import type { RateCardItem } from '@mds/shared/types/rate-card';

import { optionalText, picked, requiredText, wholeNumberText } from '@/lib/validation';

/**
 * One weekday of a room's opening hours, as the editor holds it. The API only
 * takes whole hours, so the editor works in hours and converts on the way out.
 */
export interface HoursRow {
  weekday: ResourceWeekday;
  isClosed: boolean;
  /** 0–23. */
  openHour: number;
  /** 1–24, after `openHour`. */
  closeHour: number;
}

/** The editable half of a resource, as typed. Numbers stay strings until save. */
export interface ResourceDraft {
  /**
   * The identifier bookings hold. Made from the name when the space is
   * created and immutable after that, so it is never shown or typed.
   */
  slug: string;
  name: string;
  /** Create only. */
  kind: ResourceKind;
  /** Public intake workflow. Floors always use direct requests. */
  bookingMode: ResourceBookingMode;
  /** Create only, floors only. Which seat-map artwork the floor renders. */
  seatMapKey: string;
  /** Rooms only. */
  capacity: string;
  rateItemId: string;
  /** Rooms only, in minutes. */
  minimumBookingMinutes: string;
  note: string;
  /** Public: the space cards on the client site. */
  shortDescription: string;
  /** Public: the space's own page on the client site. */
  longDescription: string;
  amenities: string[];
  /** Public: the four lines on the space's page. An empty one keeps the standard wording. */
  bookingRules: ResourceBookingRules;
  isActive: boolean;
  displayOrder: string;
  /** Rooms only: seven rows, Monday first. Floors have no hours. */
  hours: HoursRow[];
  /** Stored-file IDs in display order. */
  mediaFileIds: string[];
}

export const EMPTY_BOOKING_RULES: ResourceBookingRules = {
  booking: '',
  hours: '',
  payment: '',
  cancellation: '',
};

/** The booking rules in the order the space page lists them, with their headings there. */
export const BOOKING_RULE_FIELDS: readonly { key: keyof ResourceBookingRules; label: string }[] = [
  { key: 'booking', label: 'Booking' },
  { key: 'hours', label: 'Hours' },
  { key: 'payment', label: 'Payment' },
  { key: 'cancellation', label: 'Cancellation' },
];

/** Monday first, the order the API returns and expects. */
export const WEEKDAYS: readonly ResourceWeekday[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

/** A new room opens 09:00–22:00 every day, the same default the API applies. */
export const defaultHours = (): HoursRow[] =>
  WEEKDAYS.map((weekday) => ({ weekday, isClosed: false, openHour: 9, closeHour: 22 }));

/** The API's schedule as editor rows. A day it leaves out reads as closed. */
export const hoursOf = (hours: readonly ResourceOperatingHour[]): HoursRow[] =>
  WEEKDAYS.map((weekday) => {
    const entry = hours.find((h) => h.weekday === weekday);
    return !entry || entry.isClosed || entry.openMinute === null || entry.closeMinute === null
      ? { weekday, isClosed: true, openHour: 9, closeHour: 22 }
      : {
          weekday,
          isClosed: false,
          openHour: Math.floor(entry.openMinute / 60),
          closeHour: Math.ceil(entry.closeMinute / 60),
        };
  });

/** Editor rows back into the seven entries the API replaces the schedule with. */
export const operatingHoursOf = (rows: readonly HoursRow[]): ResourceOperatingHour[] =>
  rows.map((row) =>
    row.isClosed
      ? { weekday: row.weekday, isClosed: true, openMinute: null, closeMinute: null }
      : {
          weekday: row.weekday,
          isClosed: false,
          openMinute: row.openHour * 60,
          closeMinute: row.closeHour * 60,
        },
  );

/** A resource the API returned, back into the shape the editor edits. */
export const draftOfResource = (resource: AdminResource): ResourceDraft => ({
  slug: resource.slug,
  name: resource.name,
  kind: resource.kind,
  bookingMode: resource.bookingMode,
  seatMapKey: resource.seatMapKey ?? '',
  capacity: resource.capacity === null ? '' : String(resource.capacity),
  rateItemId: resource.rate.itemId,
  minimumBookingMinutes:
    resource.minimumBookingMinutes === null ? '' : String(resource.minimumBookingMinutes),
  note: resource.note,
  shortDescription: resource.shortDescription,
  longDescription: resource.longDescription,
  amenities: resource.amenities,
  bookingRules: resource.bookingRules ?? EMPTY_BOOKING_RULES,
  isActive: resource.isActive,
  displayOrder: String(resource.displayOrder),
  hours: resource.kind === 'room' ? hoursOf(resource.operatingHours) : defaultHours(),
  mediaFileIds: resource.media.map((item) => item.fileId),
});

export const blankDraft: ResourceDraft = {
  slug: '',
  name: '',
  kind: 'room',
  bookingMode: 'direct_request',
  seatMapKey: 'ground',
  capacity: '4',
  rateItemId: '',
  minimumBookingMinutes: '60',
  note: '',
  shortDescription: '',
  longDescription: '',
  amenities: [],
  bookingRules: EMPTY_BOOKING_RULES,
  isActive: true,
  displayOrder: '0',
  hours: defaultHours(),
  mediaFileIds: [],
};

/**
 * "Focus Room" → "focus-room", matching the slug rule the API enforces.
 * Empty when the name has nothing to build one from.
 */
export const slugFromName = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);

/** Amenities suggested in the editor. Free text is still allowed. */
export const AMENITY_POOL = [
  'Display',
  'Whiteboard',
  'Video conferencing',
  'Natural light',
  'Air conditioning',
  'Standing desk',
  'Lockers',
  'Sound treated',
];

/** Runs one rule against a value and files its first message under `path`. */
function apply(ctx: z.RefinementCtx, path: string, rule: z.ZodType, value: unknown) {
  const result = rule.safeParse(value);
  if (!result.success) {
    ctx.addIssue({ code: 'custom', path: [path], message: result.error.issues[0].message });
  }
}

const amenities = z
  .array(
    z
      .string()
      .trim()
      .min(1, 'Remove the empty amenity.')
      .max(80, 'Keep each amenity to 80 characters or fewer.'),
  )
  .max(50, 'Up to 50 amenities.')
  .refine(
    (list) => new Set(list.map((a) => a.toLowerCase())).size === list.length,
    'Each amenity should appear once.',
  );

const CAPACITY = wholeNumberText({ min: 1, max: 10_000 });
const MINIMUM_MINUTES = wholeNumberText({ min: 1, max: 1440 });

/**
 * What `POST /admin/resources` and `PATCH /admin/resources/:id` would reject.
 *
 * A room is created with a capacity and a shortest booking, but an existing
 * one may have neither, so on edit a blank clears them instead of failing.
 */
export const resourceSchema = ({
  creating,
  spaceRates,
  others,
}: {
  creating: boolean;
  spaceRates: readonly RateCardItem[];
  /** The spaces already set up, whose slugs a new one cannot take. */
  others: readonly Pick<AdminResource, 'slug' | 'name'>[];
}) =>
  z
    .object({
      name: requiredText(120),
      kind: z.enum(['room', 'floor']),
      bookingMode: z.enum(['direct_request', 'proposal']),
      rateItemId: z.string(),
      seatMapKey: z.string(),
      capacity: z.string(),
      minimumBookingMinutes: z.string(),
      note: optionalText(500),
      shortDescription: optionalText(500),
      longDescription: optionalText(20_000),
      amenities,
      bookingRules: z.object({
        booking: optionalText(1_000),
        hours: optionalText(1_000),
        payment: optionalText(1_000),
        cancellation: optionalText(1_000),
      }),
      displayOrder: wholeNumberText({ min: 0, max: 9999 }),
      hours: z.array(
        z.object({ isClosed: z.boolean(), openHour: z.number(), closeHour: z.number() }),
      ),
    })
    .superRefine((draft, ctx) => {
      const isFloor = draft.kind === 'floor';
      // A room can be priced by the hour or by the block, as the API allows.
      const units = isFloor ? ['day'] : ['hour', 'block'];

      // The identifier bookings hold is made from the name rather than asked
      // for, so the only thing to check is that two spaces cannot collide.
      if (creating && draft.name.trim() !== '') {
        const slug = slugFromName(draft.name);
        const clash = others.find((r) => r.slug === slug);
        const message =
          slug === ''
            ? 'Give it a name with a letter or number in it.'
            : clash
              ? `There is already a space called ${clash.name}. Pick a different name.`
              : null;
        if (message) ctx.addIssue({ code: 'custom', path: ['name'], message });
      }

      if (!spaceRates.some((r) => r.id === draft.rateItemId && units.includes(r.unit))) {
        ctx.addIssue({
          code: 'custom',
          path: ['rateItemId'],
          message: 'Pick the rate this space is priced from.',
        });
      }

      if (isFloor) {
        if (draft.bookingMode !== 'direct_request') {
          ctx.addIssue({
            code: 'custom',
            path: ['bookingMode'],
            message: 'Coworking floors use booking requests.',
          });
        }
        if (creating) apply(ctx, 'seatMapKey', picked('Choose the seat map.'), draft.seatMapKey);
        return;
      }
      if (creating || draft.capacity.trim() !== '') {
        apply(ctx, 'capacity', CAPACITY, draft.capacity);
      }
      if (creating || draft.minimumBookingMinutes.trim() !== '') {
        apply(ctx, 'minimumBookingMinutes', MINIMUM_MINUTES, draft.minimumBookingMinutes);
      }
      draft.hours.forEach((row, i) => {
        if (!row.isClosed && row.closeHour <= row.openHour) {
          ctx.addIssue({
            code: 'custom',
            path: ['hours', i, 'closeHour'],
            message: 'Closes before it opens. Fix the hours, or mark the day closed.',
          });
        }
      });
    });
